import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/Textarea';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { 
  Plus, 
  User, 
  Calendar as CalendarIcon, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  Send,
  Filter,
  Search,
  Users,
  Building2,
  GraduationCap,
  Settings,
  Eye,
  Edit,
  Trash2, 
  CheckSquare,
  UserPlus,
  FileText,
  Briefcase,
  DollarSign,
  Phone,
  Mail,
  MapPin,
  Download,
  MessageSquare,
  Loader2,
  CirclePlus, 
  List, 
  Target, 
  Lock, 
  Unlock
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { api } from '../services/api'; 
import { useSearchParams } from 'react-router-dom';
import TaskDetailsModal from './TaskDetailsModal';
import ConfirmDialog from './ConfirmDialog';
import { Toast } from '../components/ui/Toast';
import LoadingSpinner from './LoadingSpinner';

const TaskManagement = () => {
  // ============================================================================
  // ÉTATS PRINCIPAUX
  // ============================================================================
  
  // Utilisateur connecté
  const [currentUser, setCurrentUser] = useState(null);
  
  // Données des tâches
  const [tasks, setTasks] = useState([]);
  const [statistics, setStatistics] = useState({
    total_taches: 0,
    nouvelles: 0,
    en_cours: 0,
    terminees: 0,
    urgentes: 0,
    importantes: 0,
    demandes_employe: 0,
    en_retard: 0
  });
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // États des modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightTaskId = searchParams.get('highlight');
  
  // États de suppression
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskDataForDelete, setTaskDataForDelete] = useState(null);

  // 🆕 États pour le Toast avec durée minimale
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');


  // Auto-refresh
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState(null);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  
  // États des formulaires
  const [newTask, setNewTask] = useState({
    titre: '',
    description: '',
    priorite: 'normale',
    assigne_groupe: '',
    assigne_employe_nom: '',
    echeance: '',
    commentaire: ''
  });
  
  // États de filtrage et pagination
  const [filters, setFilters] = useState({
    statut: 'all',
    priorite: '',
    assigne_groupe: '',
    createur_id: '',
    type: '',
    search: '',
    page: 1,
    limit: 10
  });
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10
  });

  // États pour l'édition de tâche
  const [showEditModal, setShowEditModal] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({
    titre: '',
    description: '',
    priorite: 'normale',
    assigne_groupe: '',
    assigne_employe_nom: '',
    echeance: '',
    commentaire: ''
  });

  // États pour les dropdowns personnalisés
  const [isGroupeDropdownOpen, setIsGroupeDropdownOpen] = useState(false);
  const [isPrioriteDropdownOpen, setIsPrioriteDropdownOpen] = useState(false);
  const [isEmployeDropdownOpen, setIsEmployeDropdownOpen] = useState(false);
  const [isEditGroupeDropdownOpen, setIsEditGroupeDropdownOpen] = useState(false);
  const [isEditPrioriteDropdownOpen, setIsEditPrioriteDropdownOpen] = useState(false);
  const [isEditEmployeDropdownOpen, setIsEditEmployeDropdownOpen] = useState(false);
  const [employeSearchTerm, setEmployeSearchTerm] = useState('');

  // États pour les dropdowns de FILTRES
  const [isStatutFilterDropdownOpen, setIsStatutFilterDropdownOpen] = useState(false);
  const [isPrioriteFilterDropdownOpen, setIsPrioriteFilterDropdownOpen] = useState(false);

  // Données de référence
  const [employesGroupe, setEmployesGroupe] = useState({});
  const [loadingEmployes, setLoadingEmployes] = useState(false);
  
  // Fichier joint
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Refs pour fermer les dropdowns en cliquant à l'extérieur
  const groupeDropdownRef = useRef(null);
  const prioriteDropdownRef = useRef(null);
  const employeDropdownRef = useRef(null);

  // ============================================================================
  // DONNÉES DE RÉFÉRENCE
  // ============================================================================
  
  const groupes = [
    { value: 'Administration', label: 'Administration Générale' },
    { value: 'Direction', label: 'Direction Générale' },
    { value: 'Enseignants', label: 'Enseignants' },
    { value: 'RH', label: 'Ressources Humaines' },
    { value: 'Surveillance', label: 'Surveillance' },
    { value: 'Technique', label: 'Equipe Technique (Support Informatique)' }
  ];

  const priorites = [
    { value: 'normale', label: 'Normale', color: 'text-blue-600' },
    { value: 'importante', label: 'Importante', color: 'text-orange-600' },
    { value: 'urgente', label: 'Urgente', color: 'text-red-600' }
  ];

  // ============================================================================
  // EFFECTS - CHARGEMENT INITIAL
  // ============================================================================
  
  useEffect(() => {
    initializeComponent();
  }, []);
  
  useEffect(() => {
    loadTasks();
  }, [filters]);
  
  useEffect(() => {
    if (highlightTaskId) {
      setTimeout(() => {
        const taskElement = document.querySelector(`[data-task-id="${highlightTaskId}"]`);
        if (taskElement) {
          taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          taskElement.classList.add(
            'border-l-4',
            'border-l-blue-600',
            'bg-slate-50',
            'transition-all',
            'duration-700'
          );
          
          setTimeout(() => {
            setSearchParams({});
            taskElement.classList.remove(
              'border-l-4',
              'border-l-blue-600',
              'bg-slate-50',
              'transition-all',
              'duration-700'
            );
          }, 3000);
        }
      }, 500);
    }
  }, [highlightTaskId, setSearchParams, tasks]);

  // Effect pour fermer les dropdowns en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupeDropdownRef.current && !groupeDropdownRef.current.contains(event.target)) {
        setIsGroupeDropdownOpen(false);
        setIsEditGroupeDropdownOpen(false);
      }
      if (prioriteDropdownRef.current && !prioriteDropdownRef.current.contains(event.target)) {
        setIsPrioriteDropdownOpen(false);
        setIsEditPrioriteDropdownOpen(false);
      }
      if (employeDropdownRef.current && !employeDropdownRef.current.contains(event.target)) {
        setIsEmployeDropdownOpen(false);
        setIsEditEmployeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // ============================================================================
  // AUTO-REFRESH INTELLIGENT
  // ============================================================================
  
  const checkForUpdates = useCallback(async () => {
  try {
    // 🆕 VÉRIFICATION EN DÉBUT : Ne rien faire si modal/dropdown ouvert
    const isDropdownOpen = isGroupeDropdownOpen || 
                           isPrioriteDropdownOpen || 
                           isEmployeDropdownOpen ||
                           isEditGroupeDropdownOpen ||
                           isEditPrioriteDropdownOpen ||
                           isEditEmployeDropdownOpen ||
                           isStatutFilterDropdownOpen ||
                           isPrioriteFilterDropdownOpen;
    
    const isModalOpen = showCreateModal || 
                        showEditModal || 
                        showTaskDetailsModal || 
                        showDeleteConfirm;
    
    // 🆕 SORTIE IMMÉDIATE si modal ou dropdown ouvert
    if (isDropdownOpen || isModalOpen) {
      console.log('⏸️ [AutoRefresh] Modal/Dropdown ouvert, vérification ignorée');
      return;
    }
    
    setIsAutoRefreshing(true);
    
    const response = await api.taches.getLastUpdateTimestamp(filters);
    
    if (response.success) {
      const newTimestamp = response.data.timestamp;
      
      if (lastUpdateTimestamp === null) {
        console.log('🔄 [AutoRefresh] Initialisation timestamp:', newTimestamp);
        setLastUpdateTimestamp(newTimestamp);
      }
      else if (newTimestamp !== lastUpdateTimestamp) {
        console.log('🔄 [AutoRefresh] Changements détectés!', {
          ancien: lastUpdateTimestamp,
          nouveau: newTimestamp
        });
        
        setLastUpdateTimestamp(newTimestamp);
        
        // Reload silencieux en arrière-plan
        await loadTasksSilent();
        await loadStatisticsSilent();
      } else {
        console.log('✅ [AutoRefresh] Pas de changements');
      }
    }
  } catch (error) {
    console.error('❌ [AutoRefresh] Erreur vérification:', error);
  } finally {
    setIsAutoRefreshing(false);
  }
}, [
  filters, 
  lastUpdateTimestamp,
  // 🆕 AJOUT : Dépendances pour détecter si modal/dropdown ouvert
  isGroupeDropdownOpen,
  isPrioriteDropdownOpen,
  isEmployeDropdownOpen,
  isEditGroupeDropdownOpen,
  isEditPrioriteDropdownOpen,
  isEditEmployeDropdownOpen,
  isStatutFilterDropdownOpen,
  isPrioriteFilterDropdownOpen,
  showCreateModal,
  showEditModal,
  showTaskDetailsModal,
  showDeleteConfirm
]);



  useEffect(() => {
  const REFRESH_INTERVAL = 5000; // 5 secondes
  
  console.log('🔄 [AutoRefresh] Démarrage polling intelligent');
  
  checkForUpdates();
  
  const intervalId = setInterval(() => {
    // 🆕 SIMPLIFIÉ : checkForUpdates gère déjà les vérifications
    if (document.visibilityState === 'visible') {
      checkForUpdates();
    }
  }, REFRESH_INTERVAL);
  
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      console.log('👁️ [AutoRefresh] Page visible, vérification immédiate');
      checkForUpdates();
    }
  };
  
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  return () => {
    console.log('🛑 [AutoRefresh] Arrêt du polling');
    clearInterval(intervalId);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, [checkForUpdates]); // 🆕 SIMPLIFIÉ : Une seule dépendance




  // ============================================================================
  // FONCTIONS D'INITIALISATION
  // ============================================================================
  
  const initializeComponent = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id) {
        showCustomToast('Utilisateur non connecté', 'error');
        return;
      }
      setCurrentUser(user);
      
      await Promise.all([
        loadTasks(),
        loadStatistics()
      ]);
      
    } catch (error) {
      console.error('Erreur lors de l\'initialisation:', error);
      showCustomToast('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const loadTasks = async () => {
    try {
      setLoading(true);
      
      const apiFilters = api.taches.formatFilters(filters);
      const response = await api.taches.getAll(apiFilters);
      
      if (response.success) {
        setTasks(response.data || []);
        setPagination(response.pagination || {
          current_page: 1,
          total_pages: 1,
          total_items: 0,
          items_per_page: 10
        });
      } else {
        showCustomToast(response.error || 'Erreur lors du chargement des tâches', 'error');
        setTasks([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des tâches:', error);
      showCustomToast('Erreur lors du chargement des tâches', 'error');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Version silencieuse pour l'auto-refresh (sans loading visible)
  const loadTasksSilent = async () => {
    try {
      const apiFilters = api.taches.formatFilters(filters);
      const response = await api.taches.getAll(apiFilters);
      
      if (response.success) {
        setTasks(response.data || []);
        setPagination(response.pagination || {
          current_page: 1,
          total_pages: 1,
          total_items: 0,
          items_per_page: 10
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement silencieux des tâches:', error);
    }
  };
  
  const loadStatistics = async () => {
    try {
      setLoadingStats(true);
      
      const response = await api.taches.getStatistiques({
        groupe: currentUser?.groupe,
        employe_id: currentUser?.id,
        periode: '30'
      });
      
      console.log('📊 [loadStatistics] Réponse complète:', response);
      console.log('📊 [loadStatistics] Stats reçues:', response.data?.statistiques_generales);
      
      if (response.success) {
        setStatistics(response.data.statistiques_generales || {});
      } else {
        console.error('Erreur lors du chargement des statistiques:', response.error);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // 🆕 Version silencieuse pour l'auto-refresh (sans loading visible)
  const loadStatisticsSilent = async () => {
    try {
      const response = await api.taches.getStatistiques({
        groupe: currentUser?.groupe,
        employe_id: currentUser?.id,
        periode: '30'
      });
      
      if (response.success) {
        setStatistics(response.data.statistiques_generales || {});
      }
    } catch (error) {
      console.error('Erreur lors du chargement silencieux des statistiques:', error);
    }
  };
  
  const loadEmployesGroupe = async (groupe) => {
    if (!groupe || employesGroupe[groupe]) return;
    
    try {
      setLoadingEmployes(true);
      
      console.log('🔍 Chargement employés pour groupe:', groupe);
      
      const response = await api.taches.getEmployesGroupe(groupe);
      
      console.log('🔍 Réponse API employés:', response);
      
      if (response.success) {
        console.log('🔍 Structure response.data:', response.data);
        
        const employesData = response.data?.employes_par_groupe?.[groupe] || 
                             response.data?.data || 
                             response.data || 
                             [];
        
        console.log('🔍 Employés extraits:', employesData);
        
        setEmployesGroupe(prev => ({
          ...prev,
          [groupe]: Array.isArray(employesData) ? employesData : []
        }));
      } else {
        showCustomToast(`Erreur lors du chargement des employés du groupe ${groupe}`, 'error');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des employés:', error);
      showCustomToast('Erreur lors du chargement des employés', 'error');
    } finally {
      setLoadingEmployes(false);
    }
  };

  // ============================================================================
  // 🆕 FONCTIONS TOAST OPTIMISÉES
  // ============================================================================
  
  const showCustomToast = (message, type = 'success') => {
  setToastMessage(message);
  setToastType(type);
  setShowToast(true);
};



// ✅ UTILITAIRE : Exécuter une action avec délai minimum pour éviter cascade




  // ============================================================================
  // FONCTIONS UTILITAIRES ET HELPERS
  // ============================================================================

  const isMyTask = (task) => {
    if (!currentUser) return false;
    return task.assigne_groupe === currentUser.groupe || 
           task.assigne_employe_nom === `${currentUser.prenom} ${currentUser.nom}`;
  };
  
  const isMyCreation = (task) => {
    if (!currentUser) return false;
    return task.createur_id === currentUser.id;
  };
  
  const canManageTask = (task) => {
    return isMyTask(task) && !isMyCreation(task);
  };
  
  const getPriorityColor = (priorite) => {
    switch (priorite) {
      case 'urgente': return 'bg-red-500';
      case 'importante': return 'bg-orange-500';
      default: return 'bg-blue-500';
    }
  };
  
  const getStatusColor = (statut) => {
    switch (statut) {
      case 'nouveau': return 'bg-blue-100 text-blue-800';
      case 'en_cours': return 'bg-yellow-100 text-yellow-800';
      case 'termine': return 'bg-green-100 text-green-800';
      case 'annule': return 'bg-gray-100 text-gray-800';    
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusLabel = (statut) => {
    switch (statut) {
      case 'nouveau': return 'Nouveau';
      case 'en_cours': return 'En cours';
      case 'termine': return 'Terminée';
      case 'annule': return 'Annulée';  
      default: return statut;
    }
  };
  
  const getPriorityLabel = (priorite) => {
    switch (priorite) {
      case 'urgente': return 'Urgente';
      case 'importante': return 'Importante';
      case 'normale': return 'Normale';
      default: return priorite;
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy à HH:mm', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };
  
  const isOverdue = (task) => {
    if (!task.echeance || task.statut === 'termine' || task.statut === 'terminee') return false;
    const today = new Date();
    const echeance = new Date(task.echeance);
    return echeance < today;
  };

  const canDeleteTask = (task) => {
    if (!currentUser) return { allowed: false, reason: 'Utilisateur non connecté' };
    
    if (['Direction', 'SUPER_ADMIN_SECURITY', 'Administrateur système'].includes(currentUser.groupe)) {
      return { allowed: true, reason: 'Permissions administrateur' };
    }

    if (task.createur_id === currentUser.id) {
      return { allowed: true, reason: 'Créateur de la tâche' };
    }

    if (task.assigne_employe_id === currentUser.id && task.statut === 'nouveau') {
      return { allowed: true, reason: 'Tâche non commencée' };
    }

    if (currentUser.groupe === 'RH' && task.type === 'creation_employe') {
      return { allowed: true, reason: 'Demande de création d\'employé' };
    }

    return { allowed: false, reason: 'Permissions insuffisantes' };
  };

  // ============================================================================
  // GESTIONNAIRES D'ÉVÉNEMENTS
  // ============================================================================
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };
  
  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };
  
  const handleTaskFormChange = (key, value) => {
    setNewTask(prev => ({
      ...prev,
      [key]: value === 'TOUT_LE_GROUPE' ? '' : value
    }));
    
    if (key === 'assigne_groupe' && value) {
      loadEmployesGroupe(value);
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];
      
      if (file.size > maxSize) {
        showCustomToast('Le fichier ne doit pas dépasser 10MB', 'error');
        e.target.value = '';
        return;
      }
      
      if (!allowedTypes.includes(file.type)) {
        showCustomToast('Format de fichier non autorisé. Formats acceptés : PDF, DOC, DOCX, JPG, PNG', 'error');
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleEditTaskFormChange = (key, value) => {
    setEditTaskForm(prev => ({
      ...prev,
      [key]: value === 'TOUT_LE_GROUPE' ? '' : value
    }));
    
    if (key === 'assigne_groupe' && value) {
      loadEmployesGroupe(value);
    }
  };


  // ============================================================================
  // FONCTIONS DE GESTION DES TÂCHES - OPTIMISÉES
  // ============================================================================


  const handleCreateTask = async () => {
  try {
    console.log('🚀 [handleCreateTask] DÉBUT - Données du formulaire:', newTask);
    
    // Validation des données
    console.log('🔍 [handleCreateTask] Validation en cours...');
    const validation = api.taches.validateTaskData(newTask);
    console.log('🔍 [handleCreateTask] Résultat validation:', validation);
    
    if (!validation.isValid) {
      console.log('❌ [handleCreateTask] Validation échouée:', validation.errors);
      validation.errors.forEach(error => showCustomToast(error, 'error'));
      return;
    }
    
    console.log('✅ [handleCreateTask] Validation OK, début soumission...');
    
    setSubmitting(true);
    
    console.log('📤 [handleCreateTask] Appel API create avec:', {
      newTask,
      selectedFile: selectedFile ? selectedFile.name : 'aucun'
    });
    
    const response = await api.taches.create(newTask, selectedFile);
    
    console.log('📥 [handleCreateTask] Réponse API:', response);
    
    if (response.success) {
      console.log('✅ [handleCreateTask] Succès - affichage toast...');
      
      showCustomToast('Tâche créée avec succès', 'success');
      
      console.log('🔄 [handleCreateTask] Réinitialisation formulaire...');
      // Réinitialiser le formulaire
      setNewTask({
        titre: '',
        description: '',
        priorite: 'normale',
        assigne_groupe: '',
        assigne_employe_nom: '',
        echeance: '',
        commentaire: ''
      });
      setSelectedFile(null);
      setShowCreateModal(false);
      
      console.log('🔄 [handleCreateTask] Rechargement des données...');
      // Recharger les données
      await loadTasks();
      await loadStatistics();
      
      console.log('✅ [handleCreateTask] Processus terminé avec succès');
    } else {
      console.log('❌ [handleCreateTask] Erreur API:', response.error);
      showCustomToast(response.error || 'Erreur lors de la création de la tâche', 'error');
    }
  } catch (error) {
    console.error('❌ [handleCreateTask] Exception:', error);
    showCustomToast('Erreur lors de la création de la tâche', 'error');
  } finally {
    console.log('🏁 [handleCreateTask] Fin - setSubmitting(false)');
    setSubmitting(false);
  }
};



const handleOpenEditModal = async (task) => {
  try {
    console.log('✏️ [handleOpenEditModal] Ouverture édition tâche:', task.id);
    
    const response = await api.taches.getById(task.id);
    
    if (response.success) {
      const fullTask = response.data;
      setTaskToEdit(fullTask);
      
      setEditTaskForm({
        titre: fullTask.titre || '',
        description: fullTask.description || '',
        priorite: fullTask.priorite || 'normale',
        assigne_groupe: fullTask.assigne_groupe || '',
        assigne_employe_nom: fullTask.assigne_employe_nom || '',
        echeance: fullTask.echeance ? fullTask.echeance.split('T')[0] : '',
        commentaire: ''
      });
      
      if (fullTask.assigne_groupe) {
        loadEmployesGroupe(fullTask.assigne_groupe);
      }
      
      setShowEditModal(true);
    } else {
      showCustomToast('Erreur lors du chargement de la tâche', 'error');
    }
  } catch (error) {
    console.error('Erreur lors de l\'ouverture du modal d\'édition:', error);
    showCustomToast('Erreur lors du chargement de la tâche', 'error');
  }
};



const handleUpdateTask = async () => {
  try {
    console.log('💾 [handleUpdateTask] Sauvegarde modifications:', editTaskForm);
    
    const validation = api.taches.validateTaskData(editTaskForm);
    if (!validation.isValid) {
      validation.errors.forEach(error => showCustomToast(error, 'error'));
      return;
    }
    
    setSubmitting(true);
    
    const response = await api.taches.update(taskToEdit.id, editTaskForm, selectedFile);
    
    if (response.success) {
      showCustomToast('Tâche modifiée avec succès', 'success');
      
      setEditTaskForm({
        titre: '',
        description: '',
        priorite: 'normale',
        assigne_groupe: '',
        assigne_employe_nom: '',
        echeance: '',
        commentaire: ''
      });
      setSelectedFile(null);
      setTaskToEdit(null);
      setShowEditModal(false);
      
      await loadTasks();
      await loadStatistics();
    } else {
      showCustomToast(response.error || 'Erreur lors de la modification', 'error');
    }
  } catch (error) {
    console.error('Erreur lors de la modification:', error);
    showCustomToast('Erreur lors de la modification', 'error');
  } finally {
    setSubmitting(false);
  }
};




/**
 * 🔓 Déverrouiller une tâche et la réouvrir avec Optimistic UI
 */
const handleDeverrouillerTache = async (task) => {
  try {
    console.log('🔓 [deverrouiller] Déverrouillage tâche:', task.id);
    
    // 🆕 MISE À JOUR OPTIMISTE : Modifier l'UI immédiatement
    const previousTasks = [...tasks];
    const previousStats = { ...statistics };
    
    setTasks(prevTasks => 
      prevTasks.map(t => 
        t.id === task.id 
          ? { 
              ...t, 
              verrouille: 0,           // Déverrouiller
              statut: 'en_cours'       // Réouvrir en "en_cours"
            }
          : t
      )
    );
    
    // 🆕 Mettre à jour les stats localement
    setStatistics(prevStats => {
      const newStats = { ...prevStats };
      
      // Si la tâche était "termine", décrémenter les terminées et incrémenter en_cours
      if (task.statut === 'termine') {
        newStats.terminees = Math.max(0, (newStats.terminees || 0) - 1);
        newStats.en_cours = (newStats.en_cours || 0) + 1;
      }
      
      return newStats;
    });
    
    setSubmitting(true);
    
    const response = await api.taches.deverrouiller(task.id);
    
    console.log('📥 [deverrouiller] Réponse:', response);
    
    if (response.success) {
      showCustomToast('Tâche déverrouillée et réouverte avec succès', 'success');
      
      // 🆕 Reload silencieux en arrière-plan pour synchroniser
      loadTasksSilent();
      loadStatisticsSilent();
      
      // Fermer le modal de détails si ouvert
      if (showTaskDetailsModal) {
        setShowTaskDetailsModal(false);
        setSelectedTask(null);
      }
    } else {
      // 🆕 ROLLBACK en cas d'erreur
      showCustomToast(response.error || 'Erreur lors du déverrouillage', 'error');
      
      // Restaurer l'état précédent
      setTasks(previousTasks);
      setStatistics(previousStats);
      
      // Recharger pour récupérer l'état correct
      await loadTasks();
      await loadStatistics();
    }
  } catch (error) {
    console.error('Erreur lors du déverrouillage:', error);
    showCustomToast('Erreur lors du déverrouillage', 'error');
    
    // Recharger en cas d'erreur
    await loadTasks();
    await loadStatistics();
  } finally {
    setSubmitting(false);
  }
};



  // ============================================================================
  // 🆕 HANDLERS DE CHANGEMENT DE STATUT AVEC OPTIMISTIC UI
  // ============================================================================
  
  const handleChangeTaskStatus = async (taskId, newStatus, commentaire = '') => {
    try {
      console.log('🔄 [changeStatus] Changement:', { taskId, newStatus });
      
      // 🆕 MISE À JOUR OPTIMISTE : Modifier l'UI immédiatement
      const previousTasks = [...tasks];
      const previousStats = { ...statistics };
      
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, statut: newStatus }
            : task
        )
      );
      
      // 🆕 Mettre à jour les stats localement aussi
      setStatistics(prevStats => {
        const newStats = { ...prevStats };
        
        // Trouver la tâche pour connaître son ancien statut
        const task = tasks.find(t => t.id === taskId);
        const oldStatus = task?.statut;
        
        // Décrémenter l'ancien statut
        if (oldStatus === 'nouveau') {
          newStats.nouvelles = Math.max(0, (newStats.nouvelles || 0) - 1);
        } else if (oldStatus === 'en_cours') {
          newStats.en_cours = Math.max(0, (newStats.en_cours || 0) - 1);
        } else if (oldStatus === 'termine') {
          newStats.terminees = Math.max(0, (newStats.terminees || 0) - 1);
        }
        
        // Incrémenter le nouveau statut
        if (newStatus === 'nouveau') {
          newStats.nouvelles = (newStats.nouvelles || 0) + 1;
        } else if (newStatus === 'en_cours') {
          newStats.en_cours = (newStats.en_cours || 0) + 1;
        } else if (newStatus === 'termine') {
          newStats.terminees = (newStats.terminees || 0) + 1;
        }
        
        return newStats;
      });
      
      setSubmitting(true);
      
      const response = await api.taches.changerStatut(taskId, {
        statut: newStatus,
        commentaire: commentaire
      });
      
      console.log('📥 [changeStatus] Réponse:', response);
      
      if (response.success) {
        const statusLabels = {
          'nouveau': 'nouveau',
          'en_cours': 'en cours',
          'termine': 'terminée'
        };
        
        showCustomToast(`Tâche marquée comme ${statusLabels[newStatus]}`, 'success');
        
        // 🆕 Reload silencieux en arrière-plan pour synchroniser
        loadTasksSilent();
        loadStatisticsSilent();
      } else {
        // 🆕 ROLLBACK en cas d'erreur
        showCustomToast(response.error || 'Erreur lors du changement de statut', 'error');
        
        // Restaurer l'état précédent
        setTasks(previousTasks);
        setStatistics(previousStats);
        
        // Recharger pour récupérer l'état correct
        await loadTasks();
        await loadStatistics();
      }
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      showCustomToast('Erreur lors du changement de statut', 'error');
      
      // Recharger en cas d'erreur
      await loadTasks();
      await loadStatistics();
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignTask = async (taskId, employeNom, commentaire = '') => {
    try {
      setSubmitting(true);
      
      const response = await api.taches.assigner(taskId, {
        employe_nom: employeNom,
        commentaire: commentaire
      });
      
      if (response.success) {
        showCustomToast(employeNom ? 
          `Tâche assignée à ${employeNom}` : 
          'Assignation personnelle supprimée', 'success'
        );
        
        await loadTasks();
      } else {
        showCustomToast(response.error || 'Erreur lors de l\'assignation', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'assignation:', error);
      showCustomToast('Erreur lors de l\'assignation', 'error');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleAddComment = async (taskId, commentaire) => {
    try {
      if (!commentaire || commentaire.trim() === '') {
        showCustomToast('Le commentaire ne peut pas être vide', 'error');
        return;
      }
      
      setSubmitting(true);
      
      const response = await api.taches.ajouterCommentaire(taskId, {
        commentaire: commentaire.trim()
      });
      
      if (response.success) {
        showCustomToast('Commentaire ajouté avec succès', 'success');
        
        if (selectedTask && selectedTask.id === taskId) {
          await loadTaskDetails(taskId);
        }
      } else {
        showCustomToast(response.error || 'Erreur lors de l\'ajout du commentaire', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du commentaire:', error);
      showCustomToast('Erreur lors de l\'ajout du commentaire', 'error');
    } finally {
      setSubmitting(false);
    }
  };
  
  const loadTaskDetails = async (taskId) => {
    try {
      const response = await api.taches.getById(taskId);
      
      if (response.success) {
        setSelectedTask(response.data);
        setShowTaskDetailsModal(true);
      } else {
        showCustomToast(response.error || 'Erreur lors du chargement des détails', 'error');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      showCustomToast('Erreur lors du chargement des détails', 'error');
    }
  };
  
  const handleDownloadFile = async (taskId, filename) => {
    try {
      await api.taches.downloadFile(taskId, filename);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      showCustomToast('Erreur lors du téléchargement du fichier', 'error');
    }
  };



  // ============================================================================
  // 🆕 GESTION DE LA SUPPRESSION OPTIMISÉE
  // ============================================================================

  const handleOpenDeleteModal = (task) => {
    const permission = canDeleteTask(task);
    
    if (!permission.allowed) {
      showCustomToast(`Impossible de supprimer : ${permission.reason}`, 'error');
      return;
    }

    setTaskToDelete(task);
    setTaskDataForDelete(task);
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setTaskToDelete(null);
    setTaskDataForDelete(null);
  };


const handleExecuteDelete = async () => {
  try {
    if (!taskDataForDelete || !taskDataForDelete.id) {
      console.error('❌ [handleExecuteDelete] Données manquantes:', {
        taskDataForDelete,
        taskToDelete
      });
      showCustomToast('Erreur : tâche non sélectionnée', 'error');
      handleCancelDelete();
      return;
    }

    setSubmitting(true);
    
    console.log('🔄 [handleExecuteDelete] Suppression de la tâche ID:', taskDataForDelete.id);

    const response = await api.taches.delete(taskDataForDelete.id);

    if (response.success) {
      showCustomToast('Tâche supprimée avec succès', 'success');
      
      await loadTasks();
      await loadStatistics();
      
      if (selectedTask && selectedTask.id === taskDataForDelete.id) {
        setShowTaskDetailsModal(false);
        setSelectedTask(null);
      }
    } else {
      showCustomToast(response.error || 'Erreur lors de la suppression', 'error');
    }
  } catch (error) {
    console.error('Erreur lors de la suppression:', error);
    showCustomToast('Erreur lors de la suppression', 'error');
  } finally {
    setSubmitting(false);
    handleCancelDelete();
  }
};

  // ============================================================================
  // 🆕 COMPOSANTS DE DROPDOWN PERSONNALISÉS
  // ============================================================================

  // Dropdown pour la sélection de groupe
  const GroupeDropdown = ({ value, onChange, isEdit = false }) => {
    const isOpen = isEdit ? isEditGroupeDropdownOpen : isGroupeDropdownOpen;
    const setIsOpen = isEdit ? setIsEditGroupeDropdownOpen : setIsGroupeDropdownOpen;
    
    const selectedGroupe = groupes.find(g => g.value === value);
    
    return (
      <div className="relative" ref={groupeDropdownRef}>
        <div className="relative">
          <input
            type="text"
            placeholder="Sélectionner un groupe..."
            value={selectedGroupe ? selectedGroupe.label : ''}
            onFocus={() => setIsOpen(true)}
            readOnly
            className="w-full px-4 py-3 pr-10 text-base font-medium border-2 border-gray-200 rounded-lg 
                      bg-white hover:border-[#246990] focus:border-[#246990] focus:ring-4 focus:ring-[#246990]/10 
                      outline-none transition-all cursor-pointer"
          />
          
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        
        {isOpen && (
          <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {groupes.map((groupe) => {
              return (
                <div
                  key={groupe.value}
                  onClick={() => {
                    onChange(groupe.value);
                    setIsOpen(false);
                  }}
                  className={`px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors
                    ${value === groupe.value ? 'bg-blue-100' : ''}`}
                >
                  <div className="flex items-center font-medium text-gray-900 text-base">
                    {groupe.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Dropdown pour la sélection de priorité
  const PrioriteDropdown = ({ value, onChange, isEdit = false }) => {
    const isOpen = isEdit ? isEditPrioriteDropdownOpen : isPrioriteDropdownOpen;
    const setIsOpen = isEdit ? setIsEditPrioriteDropdownOpen : setIsPrioriteDropdownOpen;
    
    const selectedPriorite = priorites.find(p => p.value === value);
    
    return (
      <div className="relative" ref={prioriteDropdownRef}>
        <div className="relative">
          <input
            type="text"
            placeholder="Sélectionner une priorité..."
            value={selectedPriorite ? selectedPriorite.label : ''}
            onFocus={() => setIsOpen(true)}
            readOnly
            className="w-full px-4 py-3 pr-10 text-base font-medium border-2 border-gray-200 rounded-lg 
                      bg-white hover:border-[#c66d59] focus:border-[#c66d59] focus:ring-4 focus:ring-[#c66d59]/10 
                      outline-none transition-all cursor-pointer"
          />
          
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        
        {isOpen && (
          <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {priorites.map((priorite) => (
              <div
                key={priorite.value}
                onClick={() => {
                  onChange(priorite.value);
                  setIsOpen(false);
                }}
                className={`px-4 py-3 hover:bg-orange-50 cursor-pointer border-b last:border-b-0 transition-colors
                  ${value === priorite.value ? 'bg-orange-100' : ''}`}
              >
                <div className={`font-medium text-base ${priorite.color}`}>
                  {priorite.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Dropdown pour la sélection d'employé avec recherche
  const EmployeDropdown = ({ groupe, value, onChange, isEdit = false }) => {
    const isOpen = isEdit ? isEditEmployeDropdownOpen : isEmployeDropdownOpen;
    const setIsOpen = isEdit ? setIsEditEmployeDropdownOpen : setIsEmployeDropdownOpen;
    
    const employes = employesGroupe[groupe] || [];
    const filteredEmployes = employes.filter(emp => 
      emp.nom_complet.toLowerCase().includes(employeSearchTerm.toLowerCase())
    );
    
    return (
      <div className="relative" ref={employeDropdownRef}>
        <div className="relative">
          <Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 pointer-events-none z-10" />
          
          <input
            type="text"
            placeholder="Rechercher une personne..."
            value={value || employeSearchTerm}
            onChange={(e) => {
              const newValue = e.target.value;
              setEmployeSearchTerm(newValue);
              setIsOpen(true);
              
              // Ne réinitialiser que si le champ est complètement vide
              if (value && newValue === '') {
                onChange('');
              }
            }}
            onFocus={() => setIsOpen(true)}
            disabled={!groupe}
            className="w-full pl-10 pr-10 py-3 text-base font-medium border-2 border-gray-200 rounded-lg 
                      bg-white hover:border-[#319190] focus:border-[#319190] focus:ring-4 focus:ring-[#319190]/10 
                      outline-none transition-all
                      disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          />
          
          {/* Bouton pour effacer rapidement */}
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setEmployeSearchTerm('');
              }}
              className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        
        {isOpen && !value && groupe && (
          <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {/* Option "Tout le groupe" */}
            <div
              onClick={() => {
                onChange('');
                setEmployeSearchTerm('');
                setIsOpen(false);
              }}
              className="px-4 py-3 hover:bg-teal-50 cursor-pointer border-b transition-colors bg-teal-50"
            >
              <div className="font-medium text-gray-900 text-base">
                Tout le groupe
              </div>
            </div>
            
            {loadingEmployes ? (
              <div className="px-4 py-3 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                <span className="text-sm">Chargement...</span>
              </div>
            ) : filteredEmployes.length === 0 ? (
              <div className="px-4 py-3 text-center text-gray-500 text-sm">
                Aucun employé trouvé
              </div>
            ) : (
              filteredEmployes.map((employe) => (
                <div
                  key={employe.id}
                  onClick={() => {
                    onChange(employe.nom_complet);
                    setEmployeSearchTerm('');
                    setIsOpen(false);
                  }}
                  className="px-4 py-3 pl-8 hover:bg-teal-50 cursor-pointer border-b last:border-b-0 transition-colors"
                >
                  <div className="font-medium text-gray-900 text-base">
                    {employe.nom_complet}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // Dropdown pour le filtre de Statut
  const StatutFilterDropdown = ({ value, onChange }) => {
    const statuts = [
      { value: 'all', label: 'Tous les statuts', color: 'text-gray-700' },
      { value: 'nouveau', label: 'Nouveau', color: 'text-blue-600' },
      { value: 'en_cours', label: 'En cours', color: 'text-yellow-600' },
      { value: 'termine', label: 'Terminée', color: 'text-green-600' }
    ];
    
    const selectedStatut = statuts.find(s => s.value === value) || statuts[0];
    
    return (
      <div className="relative">
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10" size={16} />
          
          <input
            type="text"
            placeholder="Statut..."
            value={selectedStatut.label}
            onFocus={() => setIsStatutFilterDropdownOpen(true)}
            readOnly
            className="w-48 pl-10 pr-10 py-2 text-sm font-medium border-2 border-gray-200 rounded-lg 
                      bg-white hover:border-[#246990] focus:border-[#246990] focus:ring-4 focus:ring-[#246990]/10 
                      outline-none transition-all cursor-pointer"
          />
          
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        
        {isStatutFilterDropdownOpen && (
          <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {statuts.map((statut) => (
              <div
                key={statut.value}
                onClick={() => {
                  onChange(statut.value);
                  setIsStatutFilterDropdownOpen(false);
                }}
                className={`px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors
                  ${value === statut.value ? 'bg-blue-100' : ''}`}
              >
                <div className={`font-medium text-sm ${statut.color}`}>
                  {statut.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Dropdown pour le filtre de Priorité
  const PrioriteFilterDropdown = ({ value, onChange }) => {
    const prioritesFilter = [
      { value: '', label: 'Toutes les priorités', color: 'text-gray-700', emoji: '' },
      { value: 'urgente', label: 'Urgente', color: 'text-red-600', emoji: '🔴' },
      { value: 'importante', label: 'Importante', color: 'text-orange-600', emoji: '🟠' },
      { value: 'normale', label: 'Normale', color: 'text-blue-600', emoji: '🔵' }
    ];
    
    const selectedPriorite = prioritesFilter.find(p => p.value === value) || prioritesFilter[0];
    
    return (
      <div className="relative">
        <div className="relative">
          <AlertCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10" size={16} />
          
          <input
            type="text"
            placeholder="Priorité..."
            value={selectedPriorite.emoji ? `${selectedPriorite.emoji} ${selectedPriorite.label}` : selectedPriorite.label}
            onFocus={() => setIsPrioriteFilterDropdownOpen(true)}
            readOnly
            className="w-52 pl-10 pr-10 py-2 text-sm font-medium border-2 border-gray-200 rounded-lg 
                      bg-white hover:border-[#c66d59] focus:border-[#c66d59] focus:ring-4 focus:ring-[#c66d59]/10 
                      outline-none transition-all cursor-pointer"
          />
          
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        
        {isPrioriteFilterDropdownOpen && (
          <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {prioritesFilter.map((priorite) => (
              <div
                key={priorite.value}
                onClick={() => {
                  onChange(priorite.value);
                  setIsPrioriteFilterDropdownOpen(false);
                }}
                className={`px-4 py-3 hover:bg-orange-50 cursor-pointer border-b last:border-b-0 transition-colors
                  ${value === priorite.value ? 'bg-orange-100' : ''}`}
              >
                <div className={`font-medium text-sm ${priorite.color}`}>
                  {priorite.emoji && <span className="mr-2">{priorite.emoji}</span>}
                  {priorite.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  // ============================================================================
  // RENDU DU COMPOSANT
  // ============================================================================

  // Si l'utilisateur n'est pas encore chargé, afficher un loader
  if (!currentUser) {
    return <LoadingSpinner message="Chargement..." size="lg" fullHeight={true} />;
  }

  return (
    <div className="w-full p-6 space-y-8">
      {/* En-tête informatif avec guide d'utilisation */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-8 rounded-lg w-full">
        <div className="flex items-start justify-between w-full mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <CheckSquare className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Tâches Inter-Services</h1>
            </div>
          </div>

          <div className="flex gap-3">
            {/* MODAL COMPLET - NOUVELLE TÂCHE */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
              <DialogTrigger asChild>
                <Button 
                  className="flex items-center gap-2 bg-[#3084b5] hover:bg-[#2a6fa0] text-white text-base px-5 py-2 h-auto rounded-lg"
                  disabled={submitting}
                >
                  <Plus className="w-4 h-4" />
                  Nouvelle Tâche
                </Button>
              </DialogTrigger>

              <DialogContent 
                className="max-w-3xl max-h-[90vh] overflow-hidden p-0"
                onInteractOutside={(e) => e.preventDefault()}
              >
                {/* HEADER avec gradient et bouton X */}
                <div className="relative bg-gradient-to-r from-[#319190] to-[#3084b5] p-4 text-white">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      // Réinitialiser le formulaire
                      setNewTask({
                        titre: '',
                        description: '',
                        priorite: 'normale',
                        assigne_groupe: '',
                        assigne_employe_nom: '',
                        echeance: '',
                        commentaire: ''
                      });
                      setSelectedFile(null);
                    }}
                    className="absolute top-3 right-3 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1.5"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  
                  <div className="flex items-center gap-3 pr-8">
                    <div className="bg-white/20 p-2 rounded-lg">
                      <Plus className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Créer une nouvelle tâche</h2>
                      <p className="text-white/80 text-xs mt-0.5">
                        Attribuez une tâche à un groupe ou à une personne spécifique
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* CONTENU */}
                <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
                  <div className="space-y-5">
                    {/* Titre */}
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#319190]" />
                        Titre de la tâche <span className="text-red-500">*</span>
                      </label>
                      <Input 
                        value={newTask.titre}
                        onChange={(e) => handleTaskFormChange('titre', e.target.value)}
                        placeholder="Ex: Modifier les frais des élèves"
                        className="border-2 border-gray-200 focus:border-[#319190] text-base py-3"
                      />
                    </div>
                    
                    {/* Description */}
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-[#319190]" />
                        Description <span className="text-gray-400 text-xs">(10 caractères minimun)</span>
                      </label>
                      <Textarea 
                        value={newTask.description}
                        onChange={(e) => handleTaskFormChange('description', e.target.value)}
                        placeholder="Décrivez la tâche en détail..."
                        rows={3}
                        className="border-2 border-gray-200 focus:border-[#319190] text-base resize-none"
                      />
                    </div>
                    
                    {/* Priorité et Échéance */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-[#c66d59]" />
                          Priorité
                        </label>
                        <PrioriteDropdown
                          value={newTask.priorite}
                          onChange={(value) => handleTaskFormChange('priorite', value)}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4 text-[#3084b5]" />
                          Échéance
                        </label>
                        <Input
                          type="date"
                          value={newTask.echeance}
                          onChange={(e) => handleTaskFormChange('echeance', e.target.value)}
                          min={format(new Date(), 'yyyy-MM-dd')}
                          className="border-2 border-gray-200 focus:border-[#3084b5] text-base py-3"
                        />
                      </div>
                    </div>
                    
                    {/* Attribuer à */}
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#319190]" />
                        Attribuer à <span className="text-red-500">*</span>
                      </label>
                      <GroupeDropdown
                        value={newTask.assigne_groupe}
                        onChange={(value) => handleTaskFormChange('assigne_groupe', value)}
                      />
                    </div>
                    
                    {/* Personne spécifique */}
                    {newTask.assigne_groupe && (
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <User className="w-4 h-4 text-[#3084b5]" />
                          Personne spécifique <span className="text-gray-400 text-xs">(optionnel)</span>
                        </label>
                        <EmployeDropdown
                          groupe={newTask.assigne_groupe}
                          value={newTask.assigne_employe_nom}
                          onChange={(value) => handleTaskFormChange('assigne_employe_nom', value)}
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* FOOTER */}
                <div className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-500">
                    Les champs marqués d'un <span className="text-red-500">*</span> sont obligatoires
                  </p>
                  
                  <div className="flex gap-3">
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setShowCreateModal(false);
                        // Réinitialiser le formulaire
                        setNewTask({
                          titre: '',
                          description: '',
                          priorite: 'normale',
                          assigne_groupe: '',
                          assigne_employe_nom: '',
                          echeance: '',
                          commentaire: ''
                        });
                        setSelectedFile(null);
                      }}
                      className="px-6 py-2 h-auto text-gray-700 hover:bg-gray-100 border-2"
                      size="sm"
                    >
                      Annuler
                    </Button>
                    
                    <Button 
                      onClick={handleCreateTask} 
                      disabled={!newTask.titre || !newTask.description || !newTask.assigne_groupe || submitting}
                      className="px-6 py-2 h-auto bg-gradient-to-r from-[#319190] to-[#3084b5] hover:from-[#277872] hover:to-[#2a6fa0] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      size="sm"
                    >
                      <CirclePlus className="w-4 h-4 mr-2" />
                      Créer la tâche
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 text-sm w-full">
          <div className="bg-white/60 p-5 rounded-lg border border-blue-100 flex-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="font-semibold text-gray-700">Comment créer une tâche</span>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Cliquez sur "Nouvelle Tâche" → Choisissez un groupe destinataire → 
              Optionnellement une personne spécifique → Définissez la priorité et l'échéance
            </p>
          </div>

          <div className="bg-white/60 p-5 rounded-lg border border-blue-100 flex-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span className="font-semibold text-gray-700">Exemple de tâche</span>
            </div>
            <div className="text-gray-600 leading-relaxed text-sm">
              <div className="mb-2">
                <span className="font-medium">Administration :</span> Crée "Modifier frais élève TD"
              </div>
              <div className="mb-2">
                <span className="font-medium">Equipe Technique :</span> Reçoit → Commence → Modifie
              </div>
              <div>
                <span className="font-medium">Equipe Technique :</span> Termine avec commentaire ✓
              </div>
            </div>
          </div>
          
          <div className="bg-white/60 p-5 rounded-lg border border-blue-100 flex-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="font-semibold text-gray-700">Code couleur priorité</span>
            </div>
            <div className="space-y-2 text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full flex-shrink-0"></div>
                <span className="font-medium text-red-600">Urgente</span> 
                <span className="text-xs">- À traiter immédiatement</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full flex-shrink-0"></div>
                <span className="font-medium text-orange-600">Importante</span> 
                <span className="text-xs">- À traiter rapidement</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full flex-shrink-0"></div>
                <span className="font-medium text-blue-600">Normale</span> 
                <span className="text-xs">- Délai standard</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white/60 p-5 rounded-lg border border-blue-100 flex-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="font-semibold text-gray-700">Workflow des tâches</span>
            </div>
            <div className="text-gray-600 leading-relaxed">
              <div className="mb-2">
                <span className="text-blue-600">Nouveau</span> → 
                <span className="text-yellow-600"> En cours</span> → 
                <span className="text-green-600"> Terminé</span>
              </div>
              <div className="text-xs">
                Seules vos tâches assignées ont les boutons d'action
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tâches urgentes</p>
                <p className="text-2xl font-bold text-red-600">
                  {loadingStats ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    statistics.urgentes || 0
                  )}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En cours</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {loadingStats ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    statistics.en_cours || 0
                  )}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Terminées</p>
                <p className="text-2xl font-bold text-green-600">
                  {loadingStats ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    statistics.terminees || 0
                  )}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total tâches</p>
                <p className="text-2xl font-bold text-blue-600">
                  {loadingStats ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    statistics.total_taches || 0
                  )}
                </p>
              </div>
              <List className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtres et recherche */}
      <Card className="mt-8">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center space-x-4 flex-wrap gap-3">
              {/* Recherche */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                <Input 
                  placeholder="Rechercher une tâche..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 w-64 py-2"
                />
              </div>
              
              {/* Filtre Statut avec Dropdown personnalisé */}
              <StatutFilterDropdown
                value={filters.statut}
                onChange={(value) => handleFilterChange('statut', value)}
              />
              
              {/* Filtre Priorité avec Dropdown personnalisé */}
              <PrioriteFilterDropdown
                value={filters.priorite}
                onChange={(value) => handleFilterChange('priorite', value)}
              />
              
              {/* Bouton Reset Filtres */}
              {(filters.statut !== 'all' || filters.priorite || filters.search) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({
                    statut: 'all',
                    priorite: '',
                    assigne_groupe: '',
                    createur_id: '',
                    type: '',
                    search: '',
                    page: 1,
                    limit: 10
                  })}
                  className="text-gray-600 hover:text-gray-900 h-[42px]"
                >
                  ✕ Réinitialiser
                </Button>
              )}
            </div>
            
            <div className="text-sm text-gray-500 whitespace-nowrap">
              {pagination.total_items} tâche(s) trouvée(s)
              {pagination.total_pages > 1 && ` - Page ${pagination.current_page} sur ${pagination.total_pages}`}
            </div>
          </div>
        </CardContent>
      </Card>


      {/* Liste des tâches SANS overlay */}
<div className="space-y-4 mt-8">
  {/* SPINNER INITIAL - Premier chargement uniquement */}
  {loading && tasks.length === 0 ? (
    <Card>
      <CardContent className="p-12">
        <LoadingSpinner message="Chargement des tâches..." size="lg" color="#246990" />
      </CardContent>
    </Card>
  ) : tasks.length === 0 ? (
    <Card>
      <CardContent className="p-12 text-center">
        <div className="text-gray-400 mb-4">
          <Clock className="w-12 h-12 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-600 mb-2">Aucune tâche trouvée</h3>
        <p className="text-gray-500">Modifiez vos filtres ou créez une nouvelle tâche</p>
      </CardContent>
    </Card>
  ) : (
    tasks.map(task => {
      const myTask = isMyTask(task);
      const myCreation = isMyCreation(task);
      const canManage = canManageTask(task);
      const overdue = isOverdue(task);
      
      return (
        <Card 
          key={task.id} 
          data-task-id={task.id}
          className={`transition-all duration-300 hover:shadow-md ${myTask ? 'ring-2 ring-blue-200' : ''} ${overdue ? 'border-l-4 border-l-red-500' : ''}`}
        >
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold">{task.titre}</h3>
                  <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priorite)}`} />
                  <Badge className={getStatusColor(task.statut)}>
                    {getStatusLabel(task.statut)}
                  </Badge>
                  {task.type === 'creation_employe' && (
                    <Badge variant="outline" className="text-purple-600 border-purple-200 bg-purple-50">
                      <UserPlus className="w-3 h-3 mr-1" />
                      Création employé
                    </Badge>
                  )}
                  {myTask && <Badge variant="outline" className="text-blue-600">Ma tâche</Badge>}
                  {myCreation && <Badge variant="outline" className="text-green-600">Créée par moi</Badge>}
                  
                  {/* Badge Verrouillée */}
                  {Number(task.verrouille) === 1 && (
                    <Badge variant="outline" className="text-gray-600 border-gray-300 bg-gray-100">
                      <Lock className="w-3 h-3 mr-1" />
                      Verrouillée
                    </Badge>
                  )}
                  
                  {overdue && (
                    <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      En retard
                    </Badge>
                  )}
                </div>
                
                <p className="text-gray-600 mb-3 text-sm">{task.description}</p>
                
                {/* Affichage des détails de la fiche de création si c'est une tâche de création d'employé */}
                {task.type === 'creation_employe' && task.employe_nom && (
                  <div className="mb-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div>
                        <span className="font-medium text-purple-700">Employé :</span>
                        <span className="text-purple-600 ml-1">{task.employe_prenom} {task.employe_nom}</span>
                      </div>
                      <div>
                        <span className="font-medium text-purple-700">Poste :</span>
                        <span className="text-purple-600 ml-1">{task.employe_poste}</span>
                      </div>
                      <div>
                        <span className="font-medium text-purple-700">Contrat :</span>
                        <span className="text-purple-600 ml-1">{task.employe_type_contrat}</span>
                      </div>
                      <div>
                        <span className="font-medium text-purple-700">Département :</span>
                        <span className="text-purple-600 ml-1">{task.employe_departement}</span>
                      </div>
                    </div>
                    {task.employe_acces_application && (
                      <div className="mt-2 text-xs text-purple-600">
                        ✓ Accès application requis
                      </div>
                    )}
                    {task.demande_urgente && task.justification_urgence && (
                      <div className="mt-2 text-xs text-red-600 font-medium">
                        🚨 DEMANDE URGENTE : {task.justification_urgence}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center space-x-6 text-xs text-gray-500">
                  <div className="flex items-center space-x-1">
                    <User className="w-3 h-3" />
                    <span>Créée par: {task.createur_nom} ({task.createur_groupe})</span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Users className="w-3 h-3" />
                    <span>Assignée à: {task.assigne_employe_nom || task.assigne_groupe}</span>
                  </div>
                  
                  {task.echeance && (
                    <div className="flex items-center space-x-1">
                      <CalendarIcon className="w-3 h-3" />
                      <span>Échéance: {formatDate(task.echeance)}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>Créée le: {formatDate(task.date_creation)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {/* Boutons d'action pour les tâches assignées */}
                {canManage && task.statut !== 'termine' && (
                  <>
                    {task.statut === 'nouveau' && (
                      <button
                        onClick={() => handleChangeTaskStatus(task.id, 'en_cours')}
                        disabled={submitting}
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#319190] hover:bg-[#277872] active:scale-95 transition-all duration-200 text-white shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#319190]"
                        title="Commencer"
                      >
                        <Target className="w-4 h-4" />
                      </button>
                    )}
                    {task.statut === 'en_cours' && (
                      <button
                        onClick={() => handleChangeTaskStatus(task.id, 'termine')}
                        disabled={submitting}
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#319190] hover:bg-[#277872] active:scale-95 transition-all duration-200 text-white shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#319190]"
                        title="Terminer"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}

                {/* ✅ NOUVEAU : Boutons de verrouillage/déverrouillage */}
               
                {/* ✅ UNIQUEMENT : Bouton pour déverrouiller une tâche verrouillée automatiquement */}
                {Number(task.verrouille) === 1 && (
                  <div className="relative group">
                    <button
                      onClick={() => handleDeverrouillerTache(task)}
                      disabled={submitting || task.createur_id !== currentUser?.id}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 shadow-sm hover:shadow-md disabled:cursor-not-allowed ${
                        task.createur_id === currentUser?.id
                          ? 'bg-orange-500 hover:bg-orange-600 active:scale-95 text-white disabled:opacity-50 disabled:hover:bg-orange-500'
                          : 'bg-orange-300 text-orange-500 cursor-not-allowed'
                      }`}
                      title={task.createur_id === currentUser?.id ? "Déverrouiller et réouvrir" : ""}
                    >
                      <Unlock className="w-4 h-4" />
                    </button>

                    {/* Tooltip pour les non-créateurs */}
                    {task.createur_id !== currentUser?.id && (
                      <div className="absolute right-0 top-full mt-2 w-80 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-red-200">
                        <div className="flex items-start gap-2">
                          <Unlock className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-red-700 mb-1">Tâche verrouillée automatiquement</p>
                            <p className="text-gray-600">
                              Cette tâche a été <span className="font-semibold text-red-600">verrouillée automatiquement</span> lors de sa finalisation. 
                              Seul <span className="font-semibold text-red-600">{task.createur_nom}</span> (créateur) 
                              peut la déverrouiller pour permettre sa réouverture.
                            </p>
                          </div>
                        </div>
                        {/* Petite flèche */}
                        <div className="absolute -top-2 right-8 w-4 h-4 bg-white border-l border-t border-red-200 transform rotate-45"></div>
                      </div>
                    )}
                  </div>
                )}



                {/* Bouton voir détails */}
                <button
                  onClick={() => loadTaskDetails(task.id)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#3084b5] hover:bg-[#2a6fa0] active:scale-95 transition-all duration-200 text-white shadow-sm hover:shadow-md"
                  title="Voir les détails"
                >
                  <Eye className="w-4 h-4" />
                </button>

                {/* Boutons pour les créations */}
                {myCreation && (
                  <>
                    <button
                      onClick={() => handleOpenEditModal(task)}
                      disabled={submitting}
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#319190] hover:bg-[#277872] active:scale-95 transition-all duration-200 text-white shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                    </button>

                    {canDeleteTask(task).allowed && (
                      <button
                        onClick={() => handleOpenDeleteModal(task)}
                        disabled={submitting}
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#c66d59] hover:bg-[#ae5d4a] active:scale-95 transition-all duration-200 text-white shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#c66d59]"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      );
    })
  )}
</div>



      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <Card className="mt-6">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Affichage de {((pagination.current_page - 1) * pagination.items_per_page) + 1} à {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)} sur {pagination.total_items} tâches
              </div>

              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1 || loading}
                >
                  Précédent
                </Button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(pagination.total_pages, 5) }, (_, i) => {
                    let page;
                    if (pagination.total_pages <= 5) {
                      page = i + 1;
                    } else if (pagination.current_page <= 3) {
                      page = i + 1;
                    } else if (pagination.current_page >= pagination.total_pages - 2) {
                      page = pagination.total_pages - 4 + i;
                    } else {
                      page = pagination.current_page - 2 + i;
                    }

                    return (
                      <Button
                        key={page}
                        variant={pagination.current_page === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="w-8 h-8 p-0"
                        disabled={loading}
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>

                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.total_pages || loading}
                >
                  Suivant
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}


      {/* MODAL D'ÉDITION DE TÂCHE */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent 
          className="max-w-3xl max-h-[90vh] overflow-hidden p-0"
          onInteractOutside={(e) => e.preventDefault()}
        >
          {/* HEADER avec gradient et bouton X */}
          <div className="relative bg-gradient-to-r from-[#319190] to-[#3084b5] p-4 text-white">
            <button
              onClick={() => {
                setShowEditModal(false);
                setTaskToEdit(null);
                setSelectedFile(null);
                // Réinitialiser le formulaire d'édition
                setEditTaskForm({
                  titre: '',
                  description: '',
                  priorite: 'normale',
                  assigne_groupe: '',
                  assigne_employe_nom: '',
                  echeance: '',
                  commentaire: ''
                });
              }}
              className="absolute top-3 right-3 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1.5"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="flex items-center gap-3 pr-8">
              <div className="bg-white/20 p-2 rounded-lg">
                <Edit className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Modifier la tâche</h2>
                <p className="text-white/80 text-xs mt-0.5">
                  Modifiez les informations de la tâche "{taskToEdit?.titre}"
                </p>
              </div>
            </div>
          </div>

          {/* CONTENU */}
          <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
            <div className="space-y-5">
              {/* Titre */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#319190]" />
                  Titre de la tâche <span className="text-red-500">*</span>
                </label>
                <Input 
                  value={editTaskForm.titre}
                  onChange={(e) => handleEditTaskFormChange('titre', e.target.value)}
                  placeholder="Ex: Modifier les frais des élèves"
                  className="border-2 border-gray-200 focus:border-[#319190] text-base py-3"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#319190]" />
                  Description <span className="text-red-500">*</span>
                </label>
                <Textarea 
                  value={editTaskForm.description}
                  onChange={(e) => handleEditTaskFormChange('description', e.target.value)}
                  placeholder="Décrivez la tâche en détail..."
                  rows={3}
                  className="border-2 border-gray-200 focus:border-[#319190] text-base resize-none"
                />
              </div>

              {/* Priorité et Échéance */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-[#c66d59]" />
                    Priorité
                  </label>
                  <PrioriteDropdown
                    value={editTaskForm.priorite}
                    onChange={(value) => handleEditTaskFormChange('priorite', value)}
                    isEdit={true}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-[#3084b5]" />
                    Échéance
                  </label>
                  <Input
                    type="date"
                    value={editTaskForm.echeance}
                    onChange={(e) => handleEditTaskFormChange('echeance', e.target.value)}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    className="border-2 border-gray-200 focus:border-[#3084b5] text-base py-3"
                  />
                </div>
              </div>

              {/* Attribuer à */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#319190]" />
                  Attribuer à <span className="text-red-500">*</span>
                </label>
                <GroupeDropdown
                  value={editTaskForm.assigne_groupe}
                  onChange={(value) => handleEditTaskFormChange('assigne_groupe', value)}
                  isEdit={true}
                />
              </div>

              {/* Personne spécifique */}
              {editTaskForm.assigne_groupe && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#3084b5]" />
                    Personne spécifique <span className="text-gray-400 text-xs">(optionnel)</span>
                  </label>
                  <EmployeDropdown
                    groupe={editTaskForm.assigne_groupe}
                    value={editTaskForm.assigne_employe_nom}
                    onChange={(value) => handleEditTaskFormChange('assigne_employe_nom', value)}
                    isEdit={true}
                  />
                </div>
              )}

              {/* Commentaire de modification */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-gray-500" />
                  Commentaire de modification <span className="text-gray-400 text-xs">(optionnel)</span>
                </label>
                <Textarea 
                  value={editTaskForm.commentaire}
                  onChange={(e) => handleEditTaskFormChange('commentaire', e.target.value)}
                  placeholder="Expliquez les modifications apportées..."
                  rows={2}
                  className="border-2 border-gray-200 focus:border-gray-400 text-base resize-none"
                />
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              Les champs marqués d'un <span className="text-red-500">*</span> sont obligatoires
            </p>

            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setTaskToEdit(null);
                  setSelectedFile(null);
                  // Réinitialiser le formulaire d'édition
                  setEditTaskForm({
                    titre: '',
                    description: '',
                    priorite: 'normale',
                    assigne_groupe: '',
                    assigne_employe_nom: '',
                    echeance: '',
                    commentaire: ''
                  });
                }}
                className="px-6 py-2 h-auto text-gray-700 hover:bg-gray-100 border-2"
                size="sm"
              >
                Annuler
              </Button>

              <Button 
                onClick={handleUpdateTask} 
                disabled={!editTaskForm.titre || !editTaskForm.description || !editTaskForm.assigne_groupe || submitting}
                className="px-6 py-2 h-auto bg-gradient-to-r from-[#319190] to-[#3084b5] hover:from-[#277872] hover:to-[#2a6fa0] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                size="sm"
              >
                <Edit className="w-4 h-4 mr-2" />
                Enregistrer les modifications
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de détails de tâche */}
      <TaskDetailsModal
        isOpen={showTaskDetailsModal}
        onClose={() => setShowTaskDetailsModal(false)}
        selectedTask={selectedTask}
        onAddComment={handleAddComment}
        onDownloadFile={handleDownloadFile}
        submitting={submitting}
        showCustomToast={showCustomToast}
      />

      {/* Confirmation de suppression */}
      {taskDataForDelete && showDeleteConfirm && (
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={handleCancelDelete}
          onConfirm={handleExecuteDelete}
          title="Supprimer la tâche"
          description={
            <div className="space-y-3">
              <p>Voulez-vous vraiment supprimer la tâche <strong>"{taskDataForDelete.titre}"</strong> ?</p>
              <p className="text-red-600 text-sm font-medium"> Cette action est irréversible !</p>
            </div>
          }
          confirmText="Supprimer définitivement"
          cancelText="Annuler"
          type="error"
          isLoading={submitting}
        />
      )}

      {/* TOAST PERSONNALISÉ */}
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default TaskManagement;